import React, { useState, useEffect } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { Check } from 'lucide-react';
import { useAuth } from '../auth/AuthProvider';
import { useTheme } from '../theme';
import SEO from '../components/SEO';
import PageSkeleton from '../components/ui/PageSkeleton';

const PRICING = {
    regular: { monthly: 29, annual: 23 },
    founders: { monthly: 15, annual: 12 }
};

// Full-screen subscription page. This is where RequireSubscription sends an
// unsubscribed learner (see RequireSubscription.jsx). It renders WITHOUT the
// app shell — ConditionalLayout in App.js excludes /subscribe — so an
// unsubscribed learner gets a focused conversion surface, not a dead sidebar
// whose every destination just bounces back here.
//
// Two tiers, framed by persona rather than price. A parent once subscribed
// thinking it would make their child's account free, when it's the learner
// side that pays — so the free "supervisor" path is a full, equal-weight card
// here (not a subtle escape link), and the load-bearing clarifying line above
// the cards states the split outright. The supervisor card just routes to
// /family; RequireSubscription already exempts anyone who supervises a learner.
export default function SubscribePage() {
    const { isSubscribed, subscriptionLoading, ready, logout, user } = useAuth();
    const { setThemeOverride } = useTheme();
    const navigate = useNavigate();
    const [isAnnual, setIsAnnual] = useState(false);
    const [signingOut, setSigningOut] = useState(false);

    // Force a light appearance for the subscribe page. This is a non-persisting
    // override (see ThemeContext) — it does not touch the user's stored theme
    // preference, so a hard reload here can't overwrite it to 'light'
    // permanently. Only apply once we know the user is actually unsubscribed.
    useEffect(() => {
        if (!isSubscribed && ready && !subscriptionLoading) {
            setThemeOverride('light');
            return () => setThemeOverride(null);
        }
    }, [isSubscribed, ready, subscriptionLoading, setThemeOverride]);

    if (!ready || subscriptionLoading) {
        return <PageSkeleton label="Checking your subscription" />;
    }

    // A subscribed user has no business on the paywall — send them into the app.
    if (isSubscribed) return <Navigate to="/dashboard" replace />;

    // Hand off to the embedded-checkout page, carrying the chosen interval.
    // Stripe's payment form (and its own loading/error states) render there.
    const handleUpgrade = () => {
        navigate(`/subscription/checkout?interval=${isAnnual ? 'annual' : 'monthly'}`);
    };

    // Supervisor path: supervising is free, so there's nothing to buy. Send them
    // to the parent portal, where they link a learner. Once they supervise >=1,
    // RequireSubscription exempts them from this page on every future visit.
    const handleContinueFree = () => {
        navigate('/family');
    };

    // Escape hatch for someone who landed here on the wrong account (e.g. they
    // have a subscribed account but signed into a different one). The paywall
    // renders without the app shell, so there's no nav to sign out from —
    // surface it here and send them back to /login to switch accounts.
    const handleSignOut = async () => {
        if (signingOut) return;
        setSigningOut(true);
        try {
            await logout();
            navigate('/login', { replace: true });
        } catch {
            setSigningOut(false);
        }
    };

    const learnerFeatures = [
        'Unlimited AI course generation',
        'Access to our best models',
        'Personalized learning paths',
        'Progress tracking & G-Score',
    ];

    const supervisorFeatures = [
        "See a learner's progress & full transcript",
        'Assign courses to your learner',
        'Link up with a supervision code',
        'Upgrade to learn yourself anytime',
    ];

    const promoCode = process.env.REACT_APP_PROMO_CODE || '';
    const promoLabel = process.env.REACT_APP_PROMO_LABEL || 'First Month Free';

    const price = isAnnual ? PRICING.founders.annual : PRICING.founders.monthly;
    const regularPrice = isAnnual ? PRICING.regular.annual : PRICING.regular.monthly;

    const featureRow = (label) => (
        <div key={label} style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-3)' }}>
            <div style={{
                width: '20px',
                height: '20px',
                borderRadius: '50%',
                background: 'var(--success-bg)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                marginTop: '1px',
            }}>
                <Check size={12} style={{ color: 'var(--success)' }} />
            </div>
            <span style={{ fontSize: 'var(--text-body-size)', color: 'var(--text-primary)' }}>{label}</span>
        </div>
    );

    return (
        <>
            <SEO
                title="Subscribe"
                description="Unlock unlimited AI-generated courses with Graspd."
                path="/subscribe"
                noindex={true}
            />
            <div style={{
                position: 'fixed',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'auto',
                background: 'var(--bg-primary)',
                padding: 'var(--space-5)',
                boxSizing: 'border-box',
            }}>
                {/* Wrong-account escape hatch — see handleSignOut. */}
                <div className="sub-account" style={{
                    position: 'absolute',
                    top: 'var(--space-5)',
                    right: 'var(--space-5)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-3)',
                    fontSize: 'var(--text-body-size)',
                }}>
                    {user?.email && (
                        <span className="sub-account-email" style={{ color: 'var(--text-tertiary)' }}>
                            Signed in as{' '}
                            <span style={{ color: 'var(--text-secondary)', fontWeight: 600 }}>{user.email}</span>
                        </span>
                    )}
                    <button
                        onClick={handleSignOut}
                        disabled={signingOut}
                        style={{
                            background: 'var(--btn-secondary-bg)',
                            border: '1px solid var(--btn-secondary-border)',
                            color: 'var(--btn-secondary-text)',
                            borderRadius: 'var(--radius-control)',
                            padding: 'var(--space-2) var(--space-4)',
                            fontSize: 'var(--text-body-size)',
                            fontWeight: 600,
                            cursor: signingOut ? 'default' : 'pointer',
                            opacity: signingOut ? 0.6 : 1,
                            transition: 'background 0.15s ease, border-color 0.15s ease',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {signingOut ? 'Signing out…' : 'Sign out'}
                    </button>
                </div>

                <div className="sub-shell" style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 'var(--space-5)',
                    maxWidth: '760px',
                    width: '100%',
                }}>
                    {/* Header + the load-bearing clarifying line. Framed by WHO,
                        not by price, because the mistake we're fixing was a
                        confusion about which side of the relationship pays. */}
                    <div style={{ textAlign: 'center', maxWidth: '620px' }}>
                        <h1 style={{
                            fontSize: 'clamp(26px, 4vw, 34px)',
                            fontWeight: 600,
                            color: 'var(--text-primary)',
                            letterSpacing: '-0.022em',
                            margin: '0 0 var(--space-3)',
                            textWrap: 'balance',
                        }}>
                            Who is this account for?
                        </h1>
                        <p style={{
                            fontSize: 'var(--text-panel-size)',
                            color: 'var(--text-secondary)',
                            lineHeight: 1.6,
                            margin: 0,
                        }}>
                            Learners subscribe. Supervisors — a parent, a tutor, or a mentor — use
                            Graspd <strong style={{ color: 'var(--text-primary)', fontWeight: 600 }}>free</strong>.
                            Only the learner&rsquo;s own account is ever paid.
                        </p>
                    </div>

                    <div className="sub-cards" style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                        gap: 'var(--space-4)',
                        width: '100%',
                        alignItems: 'stretch',
                    }}>
                        {/* Learner (paid) */}
                        <div className="sub-card" style={{
                            display: 'flex',
                            flexDirection: 'column',
                            background: 'var(--card-bg)',
                            border: '1px solid var(--card-border)',
                            borderRadius: 'var(--radius-overlay)',
                            padding: 'var(--space-5)',
                            boxShadow: 'var(--shadow-md)',
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-3)', marginBottom: 'var(--space-4)' }}>
                                <h2 style={{ margin: 0, fontSize: 'var(--text-section-size)', fontWeight: 600, letterSpacing: '-0.018em', color: 'var(--text-primary)' }}>
                                    I&rsquo;m here to learn
                                </h2>
                                <span style={{
                                    fontSize: 'var(--text-label-size)',
                                    fontWeight: 600,
                                    letterSpacing: '0.08em',
                                    textTransform: 'uppercase',
                                    color: 'var(--accent-text)',
                                    background: 'var(--accent-light)',
                                    padding: '4px 9px',
                                    borderRadius: 'var(--radius-full)',
                                    whiteSpace: 'nowrap',
                                }}>
                                    For learners
                                </span>
                            </div>

                            {/* Monthly / annual toggle, scoped to the paid plan. */}
                            <div style={{
                                display: 'flex',
                                background: 'var(--bg-primary)',
                                borderRadius: 'var(--radius-panel)',
                                padding: 'var(--space-1)',
                                marginBottom: 'var(--space-4)',
                                border: '1px solid var(--border-subtle)',
                            }}>
                                <button
                                    onClick={() => setIsAnnual(false)}
                                    style={{
                                        flex: 1,
                                        padding: 'var(--space-2) var(--space-3)',
                                        borderRadius: 'var(--radius-control)',
                                        border: 'none',
                                        background: !isAnnual ? 'var(--text-primary)' : 'transparent',
                                        color: !isAnnual ? 'var(--bg-primary)' : 'var(--text-tertiary)',
                                        fontSize: 'var(--text-body-size)',
                                        fontWeight: 600,
                                        cursor: 'pointer',
                                        transition: 'all 0.2s',
                                    }}
                                >
                                    Monthly
                                </button>
                                <button
                                    onClick={() => setIsAnnual(true)}
                                    style={{
                                        flex: 1,
                                        padding: 'var(--space-2) var(--space-3)',
                                        borderRadius: 'var(--radius-control)',
                                        border: 'none',
                                        background: isAnnual ? 'var(--text-primary)' : 'transparent',
                                        color: isAnnual ? 'var(--bg-primary)' : 'var(--text-tertiary)',
                                        fontSize: 'var(--text-body-size)',
                                        fontWeight: 600,
                                        cursor: 'pointer',
                                        transition: 'all 0.2s',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: 'var(--space-2)',
                                    }}
                                >
                                    Annual
                                    <span style={{
                                        background: 'var(--accent-primary)',
                                        color: 'var(--text-inverted)',
                                        fontSize: 'var(--text-label-size)',
                                        fontWeight: 700,
                                        padding: '2px var(--space-2)',
                                        borderRadius: '4px',
                                    }}>
                                        -20%
                                    </span>
                                </button>
                            </div>

                            <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--space-2)', marginBottom: '2px' }}>
                                <span style={{
                                    fontSize: 'var(--text-section-size)',
                                    fontWeight: 500,
                                    color: 'var(--text-tertiary)',
                                    opacity: 0.65,
                                    textDecoration: 'line-through',
                                }}>
                                    ${regularPrice}
                                </span>
                                <span style={{ fontSize: '40px', fontWeight: 700, color: 'var(--text-primary)', letterSpacing: '-0.02em' }}>
                                    ${price}
                                </span>
                                <span style={{ fontSize: 'var(--text-body-size)', color: 'var(--text-tertiary)' }}>/ month</span>
                            </div>
                            <div style={{ fontSize: 'var(--text-body-size)', color: 'var(--text-secondary)', marginBottom: 'var(--space-4)', minHeight: '20px' }}>
                                {isAnnual
                                    ? <>billed annually <span style={{ fontWeight: 600, color: 'var(--accent-primary)' }}>($144/year)</span></>
                                    : (promoCode
                                        ? <span style={{ fontWeight: 600, color: 'var(--accent-primary)' }}>{promoLabel} · cancel anytime</span>
                                        : 'cancel anytime')}
                            </div>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)', paddingTop: 'var(--space-4)', borderTop: '1px solid var(--border-subtle)', marginBottom: 'var(--space-5)', flex: 1 }}>
                                {learnerFeatures.map(featureRow)}
                            </div>

                            <button
                                onClick={handleUpgrade}
                                style={{
                                    width: '100%',
                                    padding: 'var(--space-4) var(--space-5)',
                                    borderRadius: 'var(--radius-panel)',
                                    background: 'var(--btn-primary-bg)',
                                    color: 'var(--btn-primary-text)',
                                    fontSize: 'var(--text-panel-size)',
                                    fontWeight: 600,
                                    border: 'none',
                                    cursor: 'pointer',
                                    transition: 'background 0.2s',
                                }}
                            >
                                {`Start learning — $${price}/mo`}
                            </button>
                            <div style={{
                                marginTop: 'var(--space-3)',
                                fontSize: 'var(--text-label-size)',
                                color: 'var(--text-tertiary)',
                                textAlign: 'center',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 'var(--space-1)',
                            }}>
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                                    <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                                </svg>
                                Secure payment · Cancel anytime
                            </div>
                        </div>

                        {/* Supervisor (free) */}
                        <div className="sub-card" style={{
                            display: 'flex',
                            flexDirection: 'column',
                            background: 'var(--card-bg)',
                            border: '1px solid var(--card-border)',
                            borderRadius: 'var(--radius-overlay)',
                            padding: 'var(--space-5)',
                            boxShadow: 'var(--shadow-md)',
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-3)', marginBottom: 'var(--space-4)' }}>
                                <h2 style={{ margin: 0, fontSize: 'var(--text-section-size)', fontWeight: 600, letterSpacing: '-0.018em', color: 'var(--text-primary)' }}>
                                    I&rsquo;m supervising a learner
                                </h2>
                                <span style={{
                                    fontSize: 'var(--text-label-size)',
                                    fontWeight: 600,
                                    letterSpacing: '0.08em',
                                    textTransform: 'uppercase',
                                    color: 'var(--accent-text)',
                                    background: 'var(--accent-light)',
                                    padding: '4px 9px',
                                    borderRadius: 'var(--radius-full)',
                                    whiteSpace: 'nowrap',
                                }}>
                                    Free forever
                                </span>
                            </div>

                            <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--space-2)', marginBottom: '2px' }}>
                                <span style={{ fontSize: '40px', fontWeight: 700, color: 'var(--text-primary)', letterSpacing: '-0.02em' }}>
                                    Free
                                </span>
                            </div>
                            <div style={{ fontSize: 'var(--text-body-size)', color: 'var(--text-secondary)', marginBottom: 'var(--space-4)', minHeight: '20px' }}>
                                Watching a learner never costs anything
                            </div>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)', paddingTop: 'var(--space-4)', borderTop: '1px solid var(--border-subtle)', marginBottom: 'var(--space-5)', flex: 1 }}>
                                {supervisorFeatures.map(featureRow)}
                            </div>

                            <button
                                onClick={handleContinueFree}
                                style={{
                                    width: '100%',
                                    padding: 'var(--space-4) var(--space-5)',
                                    borderRadius: 'var(--radius-panel)',
                                    background: 'transparent',
                                    color: 'var(--accent-text)',
                                    fontSize: 'var(--text-panel-size)',
                                    fontWeight: 600,
                                    border: '1px solid var(--accent-primary)',
                                    cursor: 'pointer',
                                    transition: 'background 0.2s',
                                }}
                            >
                                Continue free
                            </button>
                            <div style={{
                                marginTop: 'var(--space-3)',
                                fontSize: 'var(--text-label-size)',
                                color: 'var(--text-tertiary)',
                                textAlign: 'center',
                            }}>
                                No card required
                            </div>
                        </div>
                    </div>

                    <p className="sub-footnote" style={{
                        textAlign: 'center',
                        fontSize: 'var(--text-body-size)',
                        color: 'var(--text-tertiary)',
                        margin: 0,
                    }}>
                        You can add your own learning, or link more learners, later.
                    </p>
                </div>
            </div>

            <style>{`
                @media (max-width: 720px) {
                    .sub-cards {
                        grid-template-columns: 1fr !important;
                    }
                    /* Keep the Sign out button reachable but drop the email
                       label so it doesn't crowd the top edge on phones. */
                    .sub-account-email {
                        display: none !important;
                    }
                }
            `}</style>
        </>
    );
}
